import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const SNOOZE_KEY = 'srs_kyc_modal_snoozed_until';

/**
 * KycAlertModal
 * Shows a reminder modal every 5 minutes for tenants who haven't completed KYC.
 * The timer only runs when the window is visible to avoid spurious fires.
 */
export default function KycAlertModal() {
    const [show, setShow] = useState(false);
    const [isEligible, setIsEligible] = useState(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const location = useLocation();

    const checkEligible = () => {
        try {
            const stored = localStorage.getItem('srs_user');
            if (!stored) return false;
            const user = JSON.parse(stored);
            if (user.kyc_verified === true) return false;
            const isTenant =
                user.role_id === 3 ||
                user.role === 'tenant' ||
                (Array.isArray(user.roles) && user.roles.some((r: any) => r === 'tenant' || r?.name === 'tenant' || r?.id === 3));
            return isTenant;
        } catch {
            return false;
        }
    };

    const scheduleNext = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            if (checkEligible() && !document.hidden) {
                setShow(true);
            }
            scheduleNext();
        }, INTERVAL_MS);
    };

    useEffect(() => {
        const eligible = checkEligible();
        setIsEligible(eligible);

        if (!eligible) return;

        // Show immediately if not snoozed (first time = after 5 min)
        scheduleNext();

        const handleUserUpdated = () => {
            const nowEligible = checkEligible();
            setIsEligible(nowEligible);
            if (!nowEligible) {
                if (timerRef.current) clearTimeout(timerRef.current);
                setShow(false);
            }
        };
        window.addEventListener('userUpdated', handleUserUpdated);
        window.addEventListener('storage', handleUserUpdated);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            window.removeEventListener('userUpdated', handleUserUpdated);
            window.removeEventListener('storage', handleUserUpdated);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Don't show on tenant dashboard — the KYC widget is right there
    const onDashboard = location.pathname.startsWith('/tenant/dashboard');

    if (!isEligible || onDashboard || !show) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={() => setShow(false)}
                style={{
                    position: 'fixed', inset: 0, zIndex: 100000,
                    background: 'rgba(29,10,59,0.55)',
                    backdropFilter: 'blur(3px)',
                    animation: 'kycFadeIn 0.25s ease',
                }}
            />

            {/* Modal */}
            <div style={{
                position: 'fixed', zIndex: 100001,
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                background: '#fff',
                borderRadius: 16,
                width: '100%',
                maxWidth: 440,
                boxShadow: '0 24px 64px rgba(29,10,59,0.3)',
                overflow: 'hidden',
                animation: 'kycSlideUp 0.3s ease',
            }}>
                {/* Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #1D0A3B, #3b1f7a)',
                    padding: '22px 24px 18px',
                    position: 'relative',
                }}>
                    <button
                        onClick={() => setShow(false)}
                        style={{
                            position: 'absolute', top: 14, right: 16,
                            background: 'rgba(255,255,255,0.12)', border: 'none',
                            color: '#fff', borderRadius: '50%', width: 30, height: 30,
                            fontSize: 16, cursor: 'pointer', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                        }}
                        aria-label="Close"
                    >
                        ×
                    </button>
                    <div style={{
                        width: 56, height: 56, borderRadius: '50%',
                        background: 'rgba(250,189,35,0.18)',
                        border: '2px solid rgba(250,189,35,0.4)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        marginBottom: 14,
                    }}>
                        <i className="fa-solid fa-shield-halved" style={{ color: '#FABD23', fontSize: 26 }} />
                    </div>
                    <h3 style={{ color: '#fff', fontWeight: 800, fontSize: 20, margin: 0, lineHeight: 1.25 }}>
                        Your Identity is Not Verified
                    </h3>
                    <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, margin: '8px 0 0' }}>
                        A quick one-time step to unlock your full SRS experience.
                    </p>
                </div>

                {/* Body */}
                <div style={{ padding: '22px 24px' }}>
                    {/* What's locked */}
                    <div style={{
                        background: 'rgba(250,189,35,0.07)',
                        border: '1px solid rgba(250,189,35,0.25)',
                        borderRadius: 10, padding: '14px 16px', marginBottom: 18,
                    }}>
                        <p style={{ fontWeight: 700, color: '#1D0A3B', fontSize: 13, margin: '0 0 10px' }}>
                            <i className="fa-solid fa-lock" style={{ color: '#C28840', marginRight: 6 }} />
                            What's locked until you verify:
                        </p>
                        {[
                            'Request viewings or inspections',
                            'Chat with landlords and agents',
                            'Submit property enquiries',
                        ].map((item) => (
                            <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                <i className="fa-solid fa-circle-xmark" style={{ color: '#dc3545', fontSize: 13, flexShrink: 0 }} />
                                <span style={{ color: '#374151', fontSize: 13 }}>{item}</span>
                            </div>
                        ))}
                    </div>

                    {/* Process steps */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
                        {[
                            { icon: 'fa-id-card', label: 'Submit ID' },
                            { icon: 'fa-magnifying-glass-chart', label: 'SRS Reviews' },
                            { icon: 'fa-unlock', label: 'Access Unlocked' },
                        ].map((step, i, arr) => (
                            <React.Fragment key={step.label}>
                                <div style={{ flex: 1, textAlign: 'center' }}>
                                    <div style={{
                                        width: 36, height: 36, borderRadius: '50%',
                                        background: 'rgba(29,10,59,0.08)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        margin: '0 auto 6px',
                                    }}>
                                        <i className={`fa-solid ${step.icon}`} style={{ color: '#1D0A3B', fontSize: 14 }} />
                                    </div>
                                    <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 600 }}>{step.label}</div>
                                </div>
                                {i < arr.length - 1 && (
                                    <div style={{ alignSelf: 'center', marginBottom: 20, color: '#FABD23', fontSize: 16 }}>→</div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* CTA */}
                    <Link
                        to="/tenant/dashboard?tab=profile"
                        onClick={() => setShow(false)}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            width: '100%', padding: '13px 0',
                            background: 'linear-gradient(135deg, #1D0A3B, #3b1f7a)',
                            color: '#FABD23', borderRadius: 10,
                            fontWeight: 700, fontSize: 15, textDecoration: 'none',
                            boxShadow: '0 4px 14px rgba(29,10,59,0.25)',
                        }}
                    >
                        <i className="fa-solid fa-shield-halved" />
                        Complete KYC Verification
                    </Link>

                    <button
                        onClick={() => setShow(false)}
                        style={{
                            display: 'block', width: '100%', marginTop: 10,
                            background: 'none', border: 'none',
                            color: '#9CA3AF', fontSize: 13, cursor: 'pointer',
                            padding: '6px 0',
                        }}
                    >
                        Remind me later
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes kycFadeIn { from { opacity: 0 } to { opacity: 1 } }
                @keyframes kycSlideUp { from { opacity: 0; transform: translate(-50%, -45%) } to { opacity: 1; transform: translate(-50%, -50%) } }
            `}</style>
        </>
    );
}
